import React from 'react';
import styled from 'styled-components';
import { restoreSanitizedFieldName } from '../forms/utils/model/fieldNameSanitizer';
import { useSmartFormContext } from '../forms/utils/useSmartFormContext';

interface MatchHintSlotProps {
  name: string;
  children?: React.ReactNode;
}

// Renders `fieldMeta[name].matchHint`, if one is set, at the exact leaf a field's own control renders. Lives at the
// leaf (TextField, DatePicker, SelectTypeahead, ...) rather than at the shared element render loop so that composite
// elements (e.g. a legal-name group of 4 TextFields) attach each hint to its own sub-field instead of stacking every
// hint for the whole element in one place.
//
// When given `children`, also wraps them with a highlighted background when `fieldMeta[name].matchHighlighted` is
// set (the auto-completed state) - the input's own control, not just the hint text below it, needs to read as
// filled-for-you. Without `children`, it's a hint-only sibling, for fields whose highlight can't sit in the same
// place as the hint - composite controls that highlight a group via MatchHighlight (a national IDs table, an SSN
// pair), or non-MUI controls that StyledHighlight below can't reach and so paint their own background (the
// document uploader's file cards).
export const MatchHintSlot: React.FC<MatchHintSlotProps> = ({ children, name }) => {
  const { fieldMeta } = useSmartFormContext();
  const meta = fieldMeta?.[restoreSanitizedFieldName(name)];

  if (!children) {
    return meta?.matchHint ?? null;
  }

  return (
    <div>
      <MatchHighlight name={name}>{children}</MatchHighlight>
      {meta?.matchHint}
    </div>
  );
};

interface MatchHighlightProps {
  name: string;
  children: React.ReactNode;
}

// Same highlighted-background treatment as MatchHintSlot's children mode, without also rendering the hint text -
// for fields that show their hint elsewhere, via a hint-only MatchHintSlot. Either because the highlight covers
// repeated sub-rows of one array field (each approval-limit row) and re-rendering the hint per row would duplicate
// it, or because it covers a whole composite group (a national IDs table, an SSN pair) whose single hint belongs
// below the group rather than inside it.
export const MatchHighlight: React.FC<MatchHighlightProps> = ({ children, name }) => {
  const { fieldMeta } = useSmartFormContext();
  const meta = fieldMeta?.[restoreSanitizedFieldName(name)];

  return <StyledHighlight $highlighted={!!meta?.matchHighlighted}>{children}</StyledHighlight>;
};

const StyledHighlight = styled.div<{ $highlighted: boolean }>`
  ${({ $highlighted, theme }) =>
    $highlighted &&
    `
      /* Targets the MUI input controls rather than filling this wrapper, which would also paint the label, hint
         and any surrounding whitespace of a composite group. Controls built on something else (e.g. the document
         uploader's file cards) match none of these selectors and so paint their own background instead.
         This app's JSS setup generates hashed class names (e.g. "MuiInputBase-root-162", no bare
         "MuiInputBase-root"), so exact-class selectors never match - use attribute substring selectors instead.
         && doubles this rule's own generated class for specificity; !important covers focus/hover overrides.
         The visible white fill lives on the inner input/select element too, not just its root container, so both
         need covering or the input's own opaque background paints over the container's highlight. */
      && [class*='MuiInputBase-root'],
      && [class*='MuiOutlinedInput-root'],
      && [class*='MuiInputBase-input'],
      && [class*='MuiOutlinedInput-input'],
      && [class*='MuiSelect-select'] {
        background-color: ${theme.colorPrimaryContainer} !important;
      }
    `}
`;
